/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import produce from "immer";
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SwitchProps,
  TextField,
  TextFieldProps,
  Typography,
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ChipInput from "material-ui-chip-input";
import ButtonWithLoading from "components/ButtonWithLoading";
import SwitchField from "components/Forms/SwitchField";
import WithIconHelper from "components/WithIconHelper";
import { isBlank } from "utils/string";
import { getErrorDescription } from "utils/apiRequestWithErrorCode";
import { useSourcesContext } from "../sourcesProvider";
import { createSource, updateSource } from "../api";
import { AdvancedFiltersAccordion, FiltersHelpLink } from "../Styled";

import SyncTimeSelector from "./SyncTimeSelector";
import { SourceConfiguration, SyncScheduleTime } from "@dashboard-v3/api";
import { Office365Settings } from "../types";
import { WritableDraft } from "immer/dist/internal";

interface Props {
  isOpen: boolean;
  isAuthorized: boolean;
  id?: string | null;
  settings?: Office365Settings;
  onClose: () => void;
}

enum Status {
  LOADING = "LOADING",
  ERROR = "ERROR",
  ADD_OR_EDIT = "ADD_OR_EDIT",
}

export default function Office365SourceDialog(props: Props) {
  const { isOpen, onClose, isAuthorized, settings, id } = props;
  const { t, actions } = useSourcesContext();
  const [form, setForm] = useState<Office365Settings>(initForm(settings));
  const [status, setStatus] = useState<Status>();
  const [errorMsg, setErrorMsg] = useState<string>();

  function updateForm(cb: (draft: WritableDraft<Office365Settings>) => void) {
    setForm(prev => produce(prev, cb));
  }

  useEffect(() => {
    if (isOpen) {
      setStatus(() => (isAuthorized ? Status.ADD_OR_EDIT : Status.LOADING));
    }
  }, [isOpen, isAuthorized]);

  async function handleSubmit() {
    const { syncScheduleTime, ...office365Settings } = form;
    setErrorMsg(null);
    try {
      setStatus(Status.LOADING);
      const body: SourceConfiguration = {
        type: "OFFICE365",
        syncScheduleTime,
        office365Settings,
      };

      if (id) {
        const source = await updateSource(id, body);
        actions.updateSource(source);
      } else {
        const source = await createSource(body);
        actions.addSource(source);
      }
      onClose();
    } catch (error) {
      const description = getErrorDescription(error);
      setStatus(Status.ERROR);

      if (isSourceFilterError(description as string)) {
        setErrorMsg(t(`error.testSource.${description}`));
      } else {
        setErrorMsg(t("error.unknownError"));
      }
    }
  }

  const handleOnChange: TextFieldProps["onChange"] = e => {
    const { name, value } = e.target;

    updateForm(draft => {
      if (isBlank(value) && name === "groupsFilterQuery") {
        draft.filterByGroup = false;
        draft.includeNestedGroupsInFilter = false;
      }

      if (isBlank(value)) {
        delete draft[name];
      } else {
        draft[name] = value;
      }
    });
  };

  function handleExcludeDomains(action: "add" | "delete") {
    return (domain: string) => {
      updateForm(draft => {
        if (action === "add") {
          draft.excludeDomains.push(domain);
        }

        if (action === "delete") {
          draft.excludeDomains = draft.excludeDomains.filter(d => d !== domain);
        }
      });
    };
  }

  const handleSwitch: SwitchProps["onChange"] = e => {
    const { name, checked } = e.target;

    updateForm(draft => {
      draft[name] = checked;

      if (name === "includeAscendantGroups" && checked) {
        draft.includeDescendantGroups = false;
      }

      if (name === "includeDescendantGroups" && checked) {
        draft.includeAscendantGroups = false;
      }

      if (!draft.includeAscendantGroups && !draft.includeDescendantGroups) {
        draft.includeNestedGroupsInFilter = false;
      }
    });
  };

  const getDialogTitle = () => {
    return id
      ? t("sources.editTypeOfSource", { type: t("sourceTypes.OFFICE365") })
      : t("sources.addTypeOfSource", { type: t("sourceTypes.OFFICE365") });
  };

  const handleSyncScheduleTime = (syncTime: SyncScheduleTime | null) => {
    updateForm(draft => {
      if (syncTime === null) {
        delete draft.syncScheduleTime;
      } else {
        draft.syncScheduleTime = syncTime;
      }
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClick={e => e.stopPropagation()}
      onClose={onClose}
      fullWidth={status !== Status.LOADING}
    >
      <DialogTitle style={{ paddingBottom: 10 }}>
        {getDialogTitle()}
      </DialogTitle>

      {status === Status.LOADING && (
        <DialogContent style={{ marginBottom: "2rem", textAlign: "center" }}>
          <CircularProgress />
        </DialogContent>
      )}

      {status === Status.ERROR && (
        <>
          <DialogContent style={{ textAlign: "center" }}>
            {t("sources.createOfficeFail", { details: errorMsg })}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{t("common:ok")}</Button>
          </DialogActions>
        </>
      )}

      {status === Status.ADD_OR_EDIT && (
        <>
          <DialogContent style={{ marginBottom: "25px", overflowY: "unset" }}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <SyncTimeSelector
                value={form.syncScheduleTime}
                onChange={handleSyncScheduleTime}
              />

              <AdvancedFiltersAccordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box>
                    <Typography variant="subtitle2">
                      {t("sources.advancedFilters.title")}
                    </Typography>
                    <Typography color="textSecondary" variant="caption">
                      {t("sources.advancedFilters.description")}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    name="usersFilterQuery"
                    label={t("sources.usersFilterQuery.label")}
                    margin="dense"
                    fullWidth
                    multiline
                    value={form.usersFilterQuery}
                    onChange={handleOnChange}
                    helperText={
                      <>
                        <Typography variant="caption">
                          Ex. “endswith(mail,'@contoso.com')”{" "}
                        </Typography>
                        <FiltersHelpLink
                          target="_blank"
                          href="https://learn.microsoft.com/en-us/graph/api/user-list?view=graph-rest-1.0&tabs=http"
                        >
                          {t("sources.usersFilterQuery.helper")}
                          <OpenInNewIcon />
                        </FiltersHelpLink>
                      </>
                    }
                  />
                  <TextField
                    name="groupsFilterQuery"
                    label={t("sources.groupsFilterQuery.label")}
                    margin="dense"
                    fullWidth
                    multiline
                    value={form.groupsFilterQuery}
                    onChange={handleOnChange}
                    helperText={
                      <>
                        <Typography variant="caption">
                          Ex. “startswith(displayName, 'Marketing')“{" "}
                        </Typography>
                        <FiltersHelpLink
                          target="_blank"
                          href="https://learn.microsoft.com/en-us/graph/api/group-list?view=graph-rest-1.0&tabs=http"
                        >
                          {t("sources.groupsFilterQuery.helper")}
                          <OpenInNewIcon />
                        </FiltersHelpLink>
                      </>
                    }
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      marginLeft: 7,
                    }}
                  >
                    <SwitchField
                      name="filterByGroup"
                      label={t("sources.filterByGroup.label")}
                      value={form.filterByGroup}
                      onChange={handleSwitch}
                      disabled={isBlank(form.groupsFilterQuery)}
                    />
                    <SwitchField
                      name="includeAscendantGroups"
                      label={t("sources.includeAscendantGroups.label")}
                      description={t("sources.includeAscendantGroups.desc")}
                      value={form.includeAscendantGroups}
                      onChange={handleSwitch}
                    />
                    <SwitchField
                      name="includeDescendantGroups"
                      label={t("sources.includeDescendantGroups.label")}
                      description={t("sources.includeDescendantGroups.desc")}
                      value={form.includeDescendantGroups}
                      onChange={handleSwitch}
                    />
                    <SwitchField
                      name="includeNestedGroupsInFilter"
                      label={t("sources.includeNestedGroupsInFilter.label")}
                      description={t(
                        "sources.includeNestedGroupsInFilter.desc"
                      )}
                      value={form.includeNestedGroupsInFilter}
                      onChange={handleSwitch}
                      disabled={
                        isBlank(form.groupsFilterQuery) ||
                        (!form.includeAscendantGroups &&
                          !form.includeDescendantGroups)
                      }
                    />
                  </div>
                  <WithIconHelper
                    position="after"
                    text={t("common:chipInputHelper")}
                    icon={<InfoOutlinedIcon fontSize="small" color="primary" />}
                    buttonStyles={{ marginLeft: "10px" }}
                  >
                    <ChipInput
                      label={t("sources.excludeDomains.label")}
                      fullWidth
                      allowDuplicates={false}
                      value={form.excludeDomains}
                      onAdd={handleExcludeDomains("add")}
                      onDelete={handleExcludeDomains("delete")}
                      helperText={
                        <Typography
                          variant="caption"
                          style={{ position: "relative", top: "4px" }}
                        >
                          {t("sources.excludeDomains.description")}
                        </Typography>
                      }
                    />
                  </WithIconHelper>
                </AccordionDetails>
              </AdvancedFiltersAccordion>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              data-testid="accounts__source-cancel-edit"
              onClick={onClose}
            >
              {t("common:cancel")}
            </Button>
            <ButtonWithLoading
              variant="contained"
              size="small"
              color="primary"
              disableElevation
              data-testid="accounts__source-save-edit"
              onClick={handleSubmit}
              loading={false}
            >
              {id ? t("buttons.saveSource") : t("buttons.addSource")}
            </ButtonWithLoading>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

const initForm = (settings: Office365Settings) => () => {
  const {
    excludeDomains = [],
    groupsFilterQuery,
    usersFilterQuery,
    filterByGroup,
    syncScheduleTime,
  } = settings || {};

  return {
    excludeDomains,
    syncScheduleTime,
    filterByGroup: filterByGroup ?? false,
    includeAscendantGroups: settings?.includeAscendantGroups ?? true,
    includeDescendantGroups: settings?.includeDescendantGroups ?? false,
    includeNestedGroupsInFilter: settings?.includeNestedGroupsInFilter ?? false,
    ...(groupsFilterQuery && { groupsFilterQuery }),
    ...(usersFilterQuery && { usersFilterQuery }),
  };
};

const isSourceFilterError = (errorDescription: string) => {
  return (
    errorDescription === "ERROR_GROUPS_FILTER" ||
    errorDescription === "ERROR_USERS_FILTER"
  );
};
